import { useState } from 'react';
import type { ArchiveSaveMode } from '../services/browser-archive-save';
import { useLocale } from '../../preferences/i18n/LocaleProvider';

export interface ExportArchiveOptions {
  fileName: string;
  title: string;
  saveMode: ArchiveSaveMode;
  syncGraphTitle: boolean;
}

interface ExportArchiveDialogProps {
  exporting?: boolean;
  initialFileName: string;
  initialTitle: string;
  warnings: string[];
  savePickerSupported: boolean;
  onCancel(): void;
  onSubmit(options: ExportArchiveOptions): Promise<void> | void;
}

export function ExportArchiveDialog({
  exporting = false,
  initialFileName,
  initialTitle,
  warnings,
  savePickerSupported,
  onCancel,
  onSubmit,
}: ExportArchiveDialogProps) {
  const { t } = useLocale();
  const [fileName, setFileName] = useState(initialFileName);
  const [title, setTitle] = useState(initialTitle);
  const [saveMode, setSaveMode] = useState<ArchiveSaveMode>(savePickerSupported ? 'picker' : 'download');
  const [syncGraphTitle, setSyncGraphTitle] = useState(true);
  const canSubmit = !exporting && fileName.trim().length > 0 && title.trim().length > 0;

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canSubmit) return;

    void onSubmit({ fileName, title, saveMode, syncGraphTitle });
  }

  return (
    <div role="dialog" aria-modal="true" aria-label={t('archiveExportDialogTitle')} className="floating-panel">
      <form className="hud-stack" onSubmit={handleSubmit} style={{ minWidth: 320, maxWidth: 380 }}>
        <h2 style={{ margin: 0, fontSize: '1rem' }}>{t('archiveExportDialogTitle')}</h2>
        <label style={{ display: 'grid', gap: 6 }}>
          <span>{t('archiveExportFileName')}</span>
          <input
            aria-label={t('archiveExportFileName')}
            className="hud-input"
            disabled={exporting}
            onChange={(event) => setFileName(event.target.value)}
            value={fileName}
          />
        </label>
        <label style={{ display: 'grid', gap: 6 }}>
          <span>{t('archiveExportTitle')}</span>
          <input
            aria-label={t('archiveExportTitle')}
            className="hud-input"
            disabled={exporting}
            onChange={(event) => setTitle(event.target.value)}
            value={title}
          />
        </label>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            aria-label={t('archiveExportSyncTitle')}
            checked={syncGraphTitle}
            disabled={exporting}
            onChange={(event) => setSyncGraphTitle(event.target.checked)}
            type="checkbox"
          />
          <span>{t('archiveExportSyncTitle')}</span>
        </label>
        {warnings.length > 0 && <p style={{ margin: 0 }}>{formatMessage(t('archiveExportWarningsPreview'), { warnings: warnings.join('; ') })}</p>}
        {savePickerSupported ? (
          <fieldset style={{ margin: 0, border: 0, padding: 0, display: 'grid', gap: 8 }}>
            <legend style={{ marginBottom: 6 }}>{t('archiveExportSaveMode')}</legend>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                checked={saveMode === 'picker'}
                disabled={exporting}
                name="archive-save-mode"
                onChange={() => setSaveMode('picker')}
                type="radio"
              />
              <span>{t('archiveSaveModePicker')}</span>
            </label>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                checked={saveMode === 'download'}
                disabled={exporting}
                name="archive-save-mode"
                onChange={() => setSaveMode('download')}
                type="radio"
              />
              <span>{t('archiveSaveModeDownload')}</span>
            </label>
          </fieldset>
        ) : (
          <p style={{ margin: 0 }}>{t('archiveSavePickerUnavailable')}</p>
        )}
        <div className="hud-toolbar" role="group" aria-label={t('archiveExportDialogTitle')}>
          <button className="hud-subtle-button" disabled={!canSubmit} type="submit">
            {exporting ? t('archiveExporting') : t('archiveExportConfirm')}
          </button>
          <button className="hud-subtle-button" disabled={exporting} onClick={onCancel} type="button">
            {t('cancel')}
          </button>
        </div>
      </form>
    </div>
  );
}

function formatMessage(template: string, values: Record<string, string>): string {
  return Object.entries(values).reduce((message, [key, value]) => message.replace(`{${key}}`, value), template);
}